import { LoadingSpinner } from "components/LoadingSpinner";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import useDocumentTitle from "hooks/useDocumentTitle";
import { useContext, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useApplyInpatientRulesetPropagationMutation,
  useGetInpatientRulesetQuery,
  useLazyGetInpatientRulesetPropagationPreviewQuery,
  useUpdateInpatientRulesetMutation,
} from "store/queries/inpatientRulesets";

import {
  InpatientRulesetForm,
  IInpatientRulesetFormData,
  INITIAL_FORM_DATA,
} from "./components/InpatientRulesetForm";
import { usePropagateAfterEdit } from "./components/usePropagateAfterEdit";
import {
  buildInpatientOutlierConfig,
  hydrateInpatientOutlierFormData,
} from "./helpers/inpatientOutlier";

export function EditInpatientRulesetPage() {
  useDocumentTitle("Edit Inpatient Ruleset");
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const [updateRuleset] = useUpdateInpatientRulesetMutation();
  const { data: rulesetData, isLoading: rulesetLoading } =
    useGetInpatientRulesetQuery(id!);

  const [formData, setFormData] =
    useState<IInpatientRulesetFormData>(INITIAL_FORM_DATA);

  const goBackToList = () => navigate("/payer-rules?tab=4");

  const { openForId, modal } = usePropagateAfterEdit({
    useLazyPreview: useLazyGetInpatientRulesetPropagationPreviewQuery as any,
    useApplyPropagation: useApplyInpatientRulesetPropagationMutation as any,
    onDone: goBackToList,
  });

  useEffect(() => {
    if (rulesetData?.data) {
      const r = rulesetData.data;
      const cfg = r.config as any;
      const ipoOverlay =
        r.type === "INPATIENT_OUTLIER"
          ? hydrateInpatientOutlierFormData(cfg)
          : {};
      setFormData({
        ...INITIAL_FORM_DATA,
        providerId: r.providerId,
        payerId: r.payerId,
        name: r.name,
        drgCodes: r.drgCodes,
        type: r.type,
        isDefault: r.isDefault ?? false,
        costToChargeRatio: cfg.costToChargeRatio ?? null,
        fixedLossThresholdInCents: cfg.fixedLossThresholdInCents ?? null,
        outlierPercent: cfg.outlierPercent ?? 80,
        thresholdInCents: cfg.thresholdInCents ?? 0,
        chargeMultiplier: cfg.chargeMultiplier ?? null,
        formula: cfg.formula ?? "BCBST Day Outlier",
        losFormula: cfg.losFormula ?? "LOS_PLUS_1",
        dischargeStatusCodes: cfg.dischargeStatusCodes ?? [],
        thresholdMultiplier: cfg.thresholdMultiplier ?? null,
        paymentPercent: cfg.paymentPercent ?? 75,
        chargeOutlierThresholdType: cfg.thresholdType ?? "FLAT",
        chargeOutlierThresholdAmountInCents: cfg.thresholdAmountInCents ?? null,
        chargeOutlierThresholdMultiplier: cfg.thresholdMultiplier ?? null,
        chargeOutlierPercent: cfg.outlierPercent ?? null,
        chargeOutlierFloorInCents: cfg.chargeFloorInCents ?? null,
        ...ipoOverlay,
      });
    }
  }, [rulesetData]);

  const handleSubmit = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      let config;
      if (formData.type === "FIXED_COST") {
        config = {
          costToChargeRatio: formData.costToChargeRatio!,
          fixedLossThresholdInCents: formData.fixedLossThresholdInCents!,
          outlierPercent: formData.outlierPercent!,
        };
      } else if (formData.type === "EXCESSIVE_LOS") {
        config = { formula: formData.formula };
      } else if (formData.type === "TRANSFER") {
        config = {
          losFormula: formData.losFormula,
          dischargeStatusCodes: formData.dischargeStatusCodes,
        };
      } else if (formData.type === "STOP_LOSS") {
        config = {
          thresholdMultiplier: formData.thresholdMultiplier!,
          paymentPercent: formData.paymentPercent!,
        };
      } else if (formData.type === "CHARGE_OUTLIER") {
        config = {
          thresholdType: formData.chargeOutlierThresholdType,
          ...(formData.chargeOutlierThresholdType === "FLAT"
            ? {
                thresholdAmountInCents:
                  formData.chargeOutlierThresholdAmountInCents!,
              }
            : {
                thresholdMultiplier: formData.chargeOutlierThresholdMultiplier!,
              }),
          outlierPercent: formData.chargeOutlierPercent!,
          ...(formData.chargeOutlierFloorInCents != null
            ? { chargeFloorInCents: formData.chargeOutlierFloorInCents }
            : {}),
        };
      } else if (formData.type === "INPATIENT_OUTLIER") {
        config = buildInpatientOutlierConfig(formData);
      } else {
        config = {
          thresholdInCents: formData.thresholdInCents!,
          chargeMultiplier: formData.chargeMultiplier!,
        };
      }

      const result = await updateRuleset({
        id,
        body: {
          providerId: formData.isDefault ? null : formData.providerId!,
          payerId: formData.isDefault ? null : formData.payerId!,
          name: formData.name.trim(),
          drgCodes: formData.drgCodes,
          type: formData.type,
          config,
          isDefault: formData.isDefault,
        } as any,
      }).unwrap();

      if (result.warning) {
        toastElement?.show({
          severity: "warn",
          summary: "Warning",
          detail: result.warning,
          life: 5000,
        });
      }

      toastElement?.show({
        severity: "success",
        summary: "Updated!",
        detail: "Inpatient ruleset updated successfully.",
      });
      if (formData.isDefault) {
        openForId(id);
      } else {
        goBackToList();
      }
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: error?.data?.message || "Failed to update inpatient ruleset",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (rulesetLoading) return <LoadingSpinner />;

  return (
    <>
      <InpatientRulesetForm
        title="Edit Inpatient Ruleset"
        submitLabel="Save Changes"
        submitIcon="pi pi-check"
        formData={formData}
        onChange={setFormData}
        onSubmit={handleSubmit}
      />
      {modal}
    </>
  );
}
